import Cookies from 'js-cookie';
import gql from 'graphql-tag';
import * as types from '../mutationTypes';
import { ME_USER_FIELDS } from '~/graphql/fragments';
import { apolloClient } from '~/plugins/apollo';
import { rewritePersistedPreferences } from '~/composables/usePersistedPreferences';

// state
export const state = {
  user: null,
  showDisconnectModal: false,
};

// getters
export const getters = {
  user: (state) => state.user,
  check: (state) => state.user !== null,
  showDisconnectModal: (state) => state.showDisconnectModal,
};

// mutations
export const mutations = {
  [types.FETCH_USER_SUCCESS](state, { user }) {
    state.user = user;
  },
  [types.LOGOUT](state) {
    state.user = null;
    Cookies.remove('token');
  },
  /**
   * Shows or hides the disconnect modal
   */
  commitShowDisconnectModal(state, show) {
    state.showDisconnectModal = show;
  },
};

// actions
export const actions = {
  async fetchUser({ commit }) {
    const { data } = await apolloClient.query({
      query: gql`
              query Me {
                  me {
                    ...MeUserFields
                    groupsExpand {
                      id
                    }
                  }
              }
              ${ME_USER_FIELDS}
          `,
    });
    // Le client lit avec `errorPolicy: 'all'` : une requête que le serveur refuse RÉSOUT,
    // sans données. Le déréférencement qui suivait levait alors une `TypeError` illisible, au
    // milieu d'une garde de navigation qui n'appelait plus `next()` — le routeur abandonnait
    // et l'écran restait vide. Le refus se dit donc lui-même, et l'appelant en décide.
    if (!data?.me) {
      throw new Error('The signed-in session carries no application user');
    }

    const distincId = `${window.keyprod?.env}_${window.tenant.name}_${data.me.id}`;
    if (window.keyprod.MIXPANEL_TOKEN && window.keyprod.MIXPANEL_TOKEN !== '') {
      import('mixpanel-browser').then(({ default: mixpanel }) => {
        mixpanel.identify(distincId);
        mixpanel.register({
          tenant_id: window.tenant.name,
          env: window.keyprod?.env,
        });
        mixpanel.start_session_recording();
      }).catch(() => {});
    }
    commit(types.FETCH_USER_SUCCESS, { user: data.me });
  },
  /**
   * Ends the session in the SPA, usually without a page reload. Clears the Apollo cache so the
   * next account signed in this tab never reads this session's data.
   * `clearStore`, not `resetStore`: active queries must not be refetched without credentials.
   */
  async logout({ commit }) {
    if (window.keyprod.MIXPANEL_TOKEN && window.keyprod.MIXPANEL_TOKEN !== '') {
      import('mixpanel-browser').then(({ default: mixpanel }) => mixpanel.stop_session_recording()).catch(() => {});
    }
    try {
      await apolloClient.clearStore();
    } catch (error) {
      // A failed clear must not keep the user signed in.
      // eslint-disable-next-line no-console
      console.error('Could not clear the Apollo cache on logout', error);
    }
    commit(types.LOGOUT);
    // The sign-out clears the whole localStorage; display preferences outlive the session.
    rewritePersistedPreferences();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
